import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';
import TrimNameInput from './TrimNameInput';

// Per Quoting_markdown/pillows.md — knife edge pillows (no trim/welt) are the
// base option; Welt/Trim/Ruffle/Insert are additive per-pillow options, all
// priced in pillowCalculations.ts (calcPillow). Trim here is a plain Yes/No
// (unlike window types' multi-placement trim) — "Yes" is stored in the
// shared trim_placement column as 'bottom' (a sentinel; pillows have no
// placement concept), matching how the SOP's Yes/No question is represented
// with the existing schema instead of adding a redundant boolean column.

type WeltType = 'none' | 'self' | 'contrast';
type InsertType = '90_10' | '50_50' | 'poly' | '';

interface State {
  widthInches: string;   // Width
  heightInches: string;  // Length
  fabricDescription: string;
  patternRepeatInches: string;
  weltType: WeltType;
  trimOn: boolean;
  trimDescription: string;
  ruffle: boolean;
  insertType: InsertType;
}

const PillowForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [state, setState] = useState<State>({
    widthInches: iv?.widthInches != null ? String(iv.widthInches) : '',
    heightInches: iv?.heightInches != null ? String(iv.heightInches) : '',
    fabricDescription: iv?.fabricDescription || '',
    patternRepeatInches: iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '',
    weltType: (iv?.weltType as WeltType) || 'none',
    trimOn: !!iv?.trimPlacement && iv.trimPlacement !== 'none',
    trimDescription: iv?.trimDescription || '',
    ruffle: !!iv?.ruffle,
    insertType: (iv?.insertType as InsertType) || '',
  });
  const [error, setError] = useState<string | null>(null);
  const s = (patch: Partial<State>) => setState((prev) => ({ ...prev, ...patch }));

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      const w = parseFloat(state.widthInches);
      const h = parseFloat(state.heightInches);
      if (!w || w <= 0 || !h || h <= 0) {
        setError('Width and length must be greater than 0');
        return null;
      }
      setError(null);
      return {
        widthInches: w,
        heightInches: h,
        fabricDescription: state.fabricDescription.trim() || null,
        patternRepeatInches: state.patternRepeatInches ? parseFloat(state.patternRepeatInches) : null,
        weltType: state.weltType,
        trimPlacement: state.trimOn ? 'bottom' : 'none',
        trimDescription: state.trimDescription.trim() || null,
        ruffle: state.ruffle,
        insertType: state.insertType || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Width (inches)</label>
          <input
            type="number" min="0" step="0.25" className="input"
            value={state.widthInches}
            onChange={(e) => s({ widthInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Length (inches)</label>
          <input
            type="number" min="0" step="0.25" className="input"
            value={state.heightInches}
            onChange={(e) => s({ heightInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Pattern Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number" min="0" step="0.25" className="input"
            placeholder="None"
            value={state.patternRepeatInches}
            onChange={(e) => s({ patternRepeatInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={state.fabricDescription}
            onChange={(name) => s({ fabricDescription: name })}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Welt</label>
          <select
            className="input"
            value={state.weltType}
            onChange={(e) => s({ weltType: e.target.value as WeltType })}
            disabled={props.disabled}
          >
            <option value="none">None (Knife Edge)</option>
            <option value="self">Self Welt</option>
            <option value="contrast">Contrast Welt</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Trim</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer', padding: '9px 0' }}>
            <input
              type="checkbox"
              checked={state.trimOn}
              onChange={(e) => s({ trimOn: e.target.checked })}
              disabled={props.disabled}
            />
            Add trim
          </label>
        </div>
        <TrimNameInput value={state.trimDescription} onChange={(v) => s({ trimDescription: v })} disabled={props.disabled} />
        <div className="form-group" style={{ flex: 1 }}>
          <label>Ruffle</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer', padding: '9px 0' }}>
            <input
              type="checkbox"
              checked={state.ruffle}
              onChange={(e) => s({ ruffle: e.target.checked })}
              disabled={props.disabled}
            />
            Add ruffle
          </label>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Insert Type</label>
          <select
            className="input"
            value={state.insertType}
            onChange={(e) => s({ insertType: e.target.value as InsertType })}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="90_10">90/10</option>
            <option value="50_50">50/50</option>
            <option value="poly">Poly</option>
          </select>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default PillowForm;
